import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Landmark, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { BrandLogo } from '../BrandLogo';

const STATUS_BADGE = {
  paid: { icon: CheckCircle2, cls: 'text-green-600', label: 'Paid' },
  pending_configuration: { icon: Clock, cls: 'text-amber-600', label: 'Pending' },
  processing: { icon: Clock, cls: 'text-amber-600', label: 'Processing' },
  failed: { icon: XCircle, cls: 'text-red-600', label: 'Failed' },
};

const BankDetailsPage = () => {
  const navigate = useNavigate();
  const [holder, setHolder] = useState('');
  const [account, setAccount] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payouts, setPayouts] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get('/api/driver/bank-details');
      setSaved(data.masked_account_number ? data : null);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get('/api/driver/payouts');
      setPayouts(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!holder.trim() || account.trim().length < 6 || ifsc.trim().length !== 11) {
      toast({ title: 'Check your details', description: 'Enter a valid account holder name, account number, and 11-character IFSC code', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/api/driver/bank-details', {
        account_holder_name: holder.trim(),
        account_number: account.trim(),
        ifsc: ifsc.trim().toUpperCase(),
      });
      setSaved(data);
      toast({ title: 'Bank details saved', description: 'Weekly payouts will be sent to this account once payouts are enabled.' });
    } catch (err) {
      toast({ title: 'Could not save', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/driver/dashboard')} data-testid="bank-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <BrandLogo variant="compact" className="h-8 w-auto" />
          <h1 className="text-xl font-bold border-l pl-4">Bank Details &amp; Payouts</h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-2xl space-y-6">
        <Card className="p-6 bg-accent/10 border-accent/30">
          <p className="text-sm text-primary">
            Your earnings from completed trips are transferred to this account every week. Add your
            bank details once, payouts run automatically after that.
          </p>
        </Card>

        {saved && (
          <Card className="p-6" data-testid="saved-bank-details">
            <div className="flex items-center gap-3">
              <Landmark className="w-8 h-8 text-primary" />
              <div>
                <p className="font-semibold">{saved.account_holder_name}</p>
                <p className="text-sm text-muted-foreground font-mono">{saved.masked_account_number} · {saved.ifsc}</p>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6">
          <h2 className="font-semibold text-lg mb-4">{saved ? 'Update Bank Account' : 'Add Bank Account'}</h2>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label htmlFor="bank-holder">Account Holder Name</Label>
              <Input id="bank-holder" value={holder} onChange={(e) => setHolder(e.target.value)} className="h-11"
                data-testid="bank-holder-input" />
            </div>
            <div>
              <Label htmlFor="bank-account">Account Number</Label>
              <Input id="bank-account" value={account} onChange={(e) => setAccount(e.target.value)} className="h-11"
                data-testid="bank-account-input" />
            </div>
            <div>
              <Label htmlFor="bank-ifsc">IFSC Code</Label>
              <Input id="bank-ifsc" value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} maxLength={11}
                className="h-11 font-mono" data-testid="bank-ifsc-input" />
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary-dark" disabled={saving} data-testid="bank-save-btn">
              {saving ? 'Saving…' : 'Save Bank Details'}
            </Button>
          </form>
        </Card>

        <Card className="p-6">
          <h2 className="font-semibold text-lg mb-4">Payout History</h2>
          {payouts.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="no-payouts-note">No payouts yet, the first weekly run will appear here.</p>
          ) : (
            <div className="space-y-3">
              {payouts.map((p) => {
                const badge = STATUS_BADGE[p.status] || STATUS_BADGE.pending_configuration;
                const Icon = badge.icon;
                return (
                  <div key={p.id} className="flex items-start justify-between border rounded-lg p-3" data-testid={`payout-${p.id}`}>
                    <div>
                      <p className="font-semibold">₹{p.amount} · {p.trip_count} trip(s)</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(p.period_start).toLocaleDateString()} - {new Date(p.period_end).toLocaleDateString()}
                      </p>
                      {p.failure_reason && <p className="text-xs text-amber-700 mt-1">{p.failure_reason}</p>}
                    </div>
                    <span className={`flex items-center gap-1 text-sm font-medium ${badge.cls}`}>
                      <Icon className="w-4 h-4" />{badge.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default BankDetailsPage;
